import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const DefectConfigContext = createContext({
  disabledLabels: [],
  processedLabels: [],
  loading: false,
  requiresOrgSelection: false,
  fetchConfig: () => Promise.resolve(),
  saveConfig: () => Promise.resolve(),
  isLabelEnabled: () => true,
});

export const useDefectConfig = () => useContext(DefectConfigContext);

export const DefectConfigProvider = ({ children }) => {
  const [disabledLabels, setDisabledLabels] = useState([]);
  const [processedLabels, setProcessedLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  // True when the backend reports there is no single org to read (SuperAdmin in
  // the all-orgs state). The Settings tab shows an org-selection prompt instead.
  const [requiresOrgSelection, setRequiresOrgSelection] = useState(false);

  const getToken = () => {
    try {
      const raw = localStorage.getItem('token');
      return raw ? raw.replace(/^"|"$/g, '') : null;
    } catch { return null; }
  };

  const fetchConfig = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}defectConfig/get`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDisabledLabels(res.data?.result?.disabled_labels || []);
      setRequiresOrgSelection(!!res.data?.requires_org_selection);
    } catch (err) {
      console.warn('Failed to fetch defect config:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProcessedLabels = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}defectConfig/labels`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProcessedLabels(res.data?.result?.labels || []);
    } catch (err) {
      console.warn('Failed to fetch processed labels:', err);
    }
  }, []);

  const saveConfig = useCallback(async (newDisabledLabels) => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}defectConfig/update`,
        { disabled_labels: newDisabledLabels },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setDisabledLabels(newDisabledLabels);
    } catch (err) {
      console.error('Failed to save defect config:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const isLabelEnabled = useCallback((label) => {
    if (!disabledLabels || disabledLabels.length === 0) return true;
    const norm = label.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    return !disabledLabels.some(d => {
      const dNorm = d.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
      return dNorm === norm || norm.includes(dNorm) || dNorm.includes(norm);
    });
  }, [disabledLabels]);

  useEffect(() => {
    const token = getToken();
    if (token) {
      fetchConfig();
      fetchProcessedLabels();
    }
  }, [fetchConfig, fetchProcessedLabels]);

  return (
    <DefectConfigContext.Provider value={{ disabledLabels, processedLabels, loading, requiresOrgSelection, fetchConfig, saveConfig, isLabelEnabled }}>
      {children}
    </DefectConfigContext.Provider>
  );
};

export default DefectConfigContext;
